import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import request from '../helpers/request';
import { useSelector, useDispatch } from 'react-redux';
import { getSubscribers } from '../redux/policiesReducer';
import {
  Sheet, Input, Modal, Grid, ModalDialog, DialogTitle, DialogContent,
  ModalClose, Stack, CircularProgress, FormControl, FormLabel, Select, Option,
  IconButton, DialogActions, ToggleButtonGroup, Chip,
} from '@mui/joy';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import WebhookCard from './WebhookCard';
import WebhookEditorDrawer from './WebhookEditorDrawer';
import WebhooksEmptyState from './WebhooksEmptyState';

const WebhooksTable = ({
  showAddModal,
  setShowAddModal,
  webhookToEdit,
  setWebhookToEdit,
  formData,
  setFormData,
}) => {
  const [search, setSearch] = useState('');
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [page, setPage] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isSaving, setIsSaving] = useState(false);

  const { policies, subscribers: webhooks = [], isSubscribersLoading: isWebhooksLoading = false } = useSelector((state) => state.policies);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSubscribers());
  }, [dispatch]);

  const handleChangeRowsPerPage = (newValue) => {
    const updatedValue = Number(newValue);
    if (updatedValue > 0) {
      setRowsPerPage(updatedValue);
      setPage(0);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const labelDisplayedRows = ({ from, to, count }) => `${from}-${to} of ${count}`;

  const handleAddWebhook = () => {
    setShowAddModal(true);
    setFormData({
      name: '',
      url: '',
      policy: '',
      fieldMappings: [],
      headers: [],
      auth_username: '',
      auth_password: '',
    });
    setIsEditing(false);
    setWebhookToEdit(null);
  };

  const handleDeleteWebhook = async () => {
    try {
      await request.delete(`/api/v1/tools/subscribers/${webhookToDelete.id}/`);
      dispatch(getSubscribers());
    } catch (e) {
      console.error('Error deleting webhook:', e);
      alert('Failed to delete webhook');
    }
    setWebhookToDelete(null);
  };

  const handleEditWebhook = (webhook) => {
    const updatedFormData = {
      name: webhook.name || '',
      url: webhook.url || '',
      policy: webhook.policy?.id || webhook.policy || '',
      fieldMappings: Array.isArray(webhook.field_mappings)
        ? webhook.field_mappings
        : Object.entries(webhook.field_mappings || {}).map(([key, value]) => ({ key, value })),
      headers: Array.isArray(webhook.headers)
        ? webhook.headers
        : Object.entries(webhook.headers || {}).map(([key, value]) => ({ key, value })),
      auth_username: webhook.auth_username || '',
      auth_password: webhook.auth_password || '',
    };
    setFormData(updatedFormData);
    setWebhookToEdit(webhook);
    setIsEditing(true);
    if (!showAddModal) {
      setShowAddModal(true);
    }
  };

  const handleSaveWebhook = async () => {
    const isEdit = !!webhookToEdit;
    const url = isEdit ? `/api/v1/tools/subscribers/${webhookToEdit.id}/` : '/api/v1/tools/subscribers/';
    const method = isEdit ? request.patch : request.post;

    setIsSaving(true);
    try {
      await method(url, {
        name: formData.name,
        url: formData.url,
        policy: formData.policy || null,
        field_mappings: formData.fieldMappings,
        headers: formData.headers,
        auth_username: formData.auth_username || null,
        auth_password: formData.auth_password || null,
      });
      dispatch(getSubscribers());
      setWebhookToEdit(null);
      setShowAddModal(false);
    } catch (e) {
      console.error(`Error ${isEdit ? 'updating' : 'adding'} webhook:`, e);
      alert(`Failed to ${isEdit ? 'update' : 'add'} webhook`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseDrawer = () => {
    setShowAddModal(false);
    setWebhookToEdit(null);
  };

  const handleFilter = (webhook) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    
    return (
      webhook.id.toString().includes(searchLower) ||
      webhook.name.toLowerCase().includes(searchLower) ||
      webhook.url.toLowerCase().includes(searchLower) ||
      (policies.find(p => p.id === (webhook.policy?.id || webhook.policy))?.name || '').toLowerCase().includes(searchLower)
    );
  };

  const filteredWebhooks = webhooks.filter(handleFilter);
  const paginatedRows = filteredWebhooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getPolicyName = (webhook) => {
    const policyId = webhook.policy?.id || webhook.policy;
    return policies.find(p => p.id === policyId)?.name || null;
  };

  const selectedPolicy = useMemo(
    () => policies.find(p => p.id === formData.policy),
    [formData.policy, policies]
  );

  useEffect(() => {
    if (isEditing) return;
    if (!formData.policy || !selectedPolicy) {
      setFormData(prev => ({
        ...prev,
        fieldMappings: [],
      }));
      return;
    }

    const schema = selectedPolicy.policy?.documentFieldsSchema;

    if (Array.isArray(schema)) {
      const fieldMappings = schema.map(field => ({
        key: field.key || '',
        value: '',
      }));

      setFormData(prev => ({
        ...prev,
        fieldMappings,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        fieldMappings: [],
      }));
    }
  }, [formData.policy, selectedPolicy, isEditing]);

  // Show empty state when no webhooks
  if (!isWebhooksLoading && webhooks.length === 0) {
    return (
      <>
        <WebhooksEmptyState onAddNew={handleAddWebhook} />
        <WebhookEditorDrawer
          open={showAddModal}
          onClose={handleCloseDrawer}
          onSave={handleSaveWebhook}
          webhook={webhookToEdit}
          policies={policies}
          formData={formData}
          setFormData={setFormData}
          isLoading={isSaving}
        />
      </>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Input
          placeholder="Search webhooks..."
          value={search}
          startDecorator={<SearchIcon sx={{ color: 'neutral.500' }} />}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />

        <ToggleButtonGroup
          value={viewMode}
          onChange={(_, val) => val && setViewMode(val)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          <IconButton value="grid" aria-label="Grid view">
            <GridViewRoundedIcon />
          </IconButton>
          <IconButton value="list" aria-label="List view">
            <ViewListRoundedIcon />
          </IconButton>
        </ToggleButtonGroup>

        <IconButton
          variant="outlined"
          color="neutral"
          onClick={() => dispatch(getSubscribers())}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          <RefreshIcon />
        </IconButton>

        <Button
          startDecorator={<AddRoundedIcon />}
          onClick={handleAddWebhook}
          color="primary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add Webhook
        </Button>
      </Stack>

      {/* Content */}
      <Box>
        {isWebhooksLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size="lg" />
            <Typography level="body-md" sx={{ mt: 2 }} color="neutral">
              Loading webhooks...
            </Typography>
          </Stack>
        ) : filteredWebhooks.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <Typography level="body-lg" color="neutral">
              No webhooks match your search
            </Typography>
            <Button
              variant="plain"
              onClick={() => setSearch('')}
              sx={{ mt: 1 }}
            >
              Clear search
            </Button>
          </Stack>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <Grid container spacing={2}>
                {paginatedRows.map((webhook, index) => (
                  <Grid key={webhook.id} xs={12} sm={6} md={4} lg={3}>
                    <WebhookCard
                      webhook={webhook}
                      policyName={getPolicyName(webhook)}
                      onEdit={handleEditWebhook}
                      onDelete={setWebhookToDelete}
                      index={index}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* List View - Compact cards */}
            {viewMode === 'list' && (
              <Stack spacing={1}>
                {paginatedRows.map((webhook) => (
                  <Sheet
                    key={webhook.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 'md',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: 'background.level1',
                        borderColor: 'primary.300',
                      },
                    }}
                    onClick={() => handleEditWebhook(webhook)}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 'sm',
                        bgcolor: 'primary.softBg',
                        color: 'primary.600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                        {webhook.name?.charAt(0)?.toUpperCase() || 'W'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                        {webhook.name}
                      </Typography>
                      <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {webhook.url}
                      </Typography>
                    </Box>
                    {getPolicyName(webhook) && (
                      <Chip size="sm" variant="soft" color="neutral">
                        {getPolicyName(webhook)}
                      </Chip>
                    )}
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setWebhookToDelete(webhook);
                      }}
                    >
                      ×
                    </IconButton>
                  </Sheet>
                ))}
              </Stack>
            )}

            {/* Pagination */}
            {filteredWebhooks.length > rowsPerPage && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <FormControl orientation="horizontal" size="sm">
                  <FormLabel>Per page:</FormLabel>
                  <Select
                    value={rowsPerPage}
                    onChange={(_, newValue) => handleChangeRowsPerPage(newValue)}
                    sx={{ minWidth: 70 }}
                  >
                    <Option value={6}>6</Option>
                    <Option value={12}>12</Option>
                    <Option value={24}>24</Option>
                    <Option value={48}>48</Option>
                  </Select>
                </FormControl>
                <Typography level="body-sm" sx={{ textAlign: 'center', minWidth: 80 }}>
                  {labelDisplayedRows({
                    from: filteredWebhooks.length === 0 ? 0 : page * rowsPerPage + 1,
                    to: Math.min(filteredWebhooks.length, (page + 1) * rowsPerPage),
                    count: filteredWebhooks.length,
                  })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => handleChangePage(page - 1)}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    disabled={page >= Math.ceil(filteredWebhooks.length / rowsPerPage) - 1}
                    onClick={() => handleChangePage(page + 1)}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Delete Modal */}
      <Modal open={!!webhookToDelete} onClose={() => setWebhookToDelete(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <ModalClose />
          <DialogTitle>Delete Webhook</DialogTitle>
          <DialogContent>
            <Typography level="body-md">
              Are you sure you want to delete <strong>{webhookToDelete?.name}</strong>?
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              This action cannot be undone. The webhook will stop receiving extraction results.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="danger" onClick={handleDeleteWebhook}>
              Delete
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setWebhookToDelete(null)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Add/Edit Drawer */}
      <WebhookEditorDrawer
        open={showAddModal || !!webhookToEdit}
        onClose={handleCloseDrawer}
        onSave={handleSaveWebhook}
        webhook={webhookToEdit}
        policies={policies}
        formData={formData}
        setFormData={setFormData}
        isLoading={isSaving}
      />
    </Box>
  );
};

export default WebhooksTable;
